// Discharge Rate Characteristics - Voltage and Temperature curves at different C-rates
// Based on MATLAB: DCC_Ivar plots (V vs Q and T_surf vs Q)
// Supports split plots with synchronized hover behavior

import { useEffect, useRef } from 'preact/hooks'
import * as echarts from 'echarts'

// Store chart instances globally for synchronization
const chartRegistry = {}

export default function DischargeRateCharacteristics({ 
  cellData, 
  plotId = 'default',
  showLegend = false,
  showTemperature = false
}) {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!cellData?.electrical) return

    const {
      DCC_Ivar_t = [],
      DCC_Ivar_Q = [],
      DCC_Ivar_V = [],
      DCC_Ivar_T_surf = [],
      DCC_Ivar_C_rate = [],
      DCC_Ivar_T_amb = []
    } = cellData.electrical

    if (DCC_Ivar_t.length === 0) return

    // Find split points where time decreases (new discharge cycle)
    const splitIndices = [0]
    for (let i = 1; i < DCC_Ivar_t.length; i++) {
      if (DCC_Ivar_t[i] < DCC_Ivar_t[i - 1]) {
        splitIndices.push(i)
      }
    }
    splitIndices.push(DCC_Ivar_t.length)

    // Extract curves for each C-rate
    const seriesData = []
    // High contrast color palette - alternating warm and cool tones
    const colors = ['#ea5b0c', '#0077b6', '#d62828', '#48cae4', '#ff9500', '#00b4d8', '#cc4a00', '#90e0ef']

    let maxCapacity = 0
    let minValue = Infinity
    let maxValue = -Infinity

    for (let i = 0; i < splitIndices.length - 1; i++) {
      const start = splitIndices[i]
      const end = splitIndices[i + 1]
      
      const Q_slice = DCC_Ivar_Q.slice(start, end)
      const V_slice = DCC_Ivar_V.slice(start, end)
      const T_slice = DCC_Ivar_T_surf.slice(start, end)
      
      const cRate = Math.abs(DCC_Ivar_C_rate[i] || 0).toFixed(1)
      const T_amb = (DCC_Ivar_T_amb[i] || 25).toFixed(0)  // Already in °C from dataService
      const label = `${cRate} C @ ${T_amb} °C`

      // Capacity data - already in Ah from dataService normalization
      const data = Q_slice.map((q, idx) => {
        const cap_Ah = q  // Already converted to Ah in dataService (C → Ah via /3600)
        maxCapacity = Math.max(maxCapacity, cap_Ah)
        
        const value = showTemperature 
          ? T_slice[idx]  // Already in °C from dataService (K → °C via -273.15)
          : V_slice[idx]  // Voltage in V
        
        minValue = Math.min(minValue, value)
        maxValue = Math.max(maxValue, value)
        
        return [cap_Ah, value]
      })

      seriesData.push({
        name: label,
        type: 'line',
        data: data,
        smooth: false,
        symbol: 'none',
        lineStyle: { width: 2 },
        color: colors[i % colors.length],
        emphasis: {
          focus: 'none'
        }
      })
    }

    // Create chart
    if (chartRef.current) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'svg' })
        chartRegistry[plotId] = chartInstance.current
      }

      const yAxisLabel = showTemperature ? 'T{sub|surf} / °C' : 'Voltage / V'
      
      const option = {
        backgroundColor: 'transparent',
        grid: {
          left: 60,
          right: 30,
          top: showLegend ? 45 : 20,
          bottom: 50
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(20, 20, 20, 0.95)',
          borderColor: 'rgba(234, 91, 12, 0.3)',
          textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 11 },
          confine: true,
          order: 'seriesAsc',
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: 'rgba(234, 91, 12, 0.8)',
              width: 1,
              type: 'dashed'
            },
            axis: 'x',
            snap: false
          },
          formatter: (params) => {
            if (!params || params.length === 0) return ''
            const unit = showTemperature ? '°C' : 'V'
            const decimals = showTemperature ? 1 : 3
            
            // Get X position from axis value
            const xValue = params[0].axisValue
            let result = `<b>Capacity: ${xValue.toFixed(2)} Ah</b><br/>`
            
            // Manually interpolate values for all series at this X position
            seriesData.forEach((series, idx) => {
              const color = colors[idx % colors.length]
              const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>`
              
              // Find interpolated Y value at xValue
              let yValue = null
              for (let i = 0; i < series.data.length - 1; i++) {
                const x1 = series.data[i][0]
                const x2 = series.data[i + 1][0]
                if (xValue >= x1 && xValue <= x2) {
                  const y1 = series.data[i][1]
                  const y2 = series.data[i + 1][1]
                  // Linear interpolation
                  yValue = y1 + (y2 - y1) * (xValue - x1) / (x2 - x1)
                  break
                }
              }
              
              if (yValue !== null) {
                result += `${marker} ${series.name}: ${yValue.toFixed(decimals)} ${unit}<br/>`
              }
            })
            
            return result
          }
        },
        legend: {
          show: showLegend,
          top: 5,
          textStyle: {
            fontFamily: 'Roboto',
            fontSize: 10,
            color: '#ccc'
          }
        },
        xAxis: {
          type: 'value',
          name: 'Capacity / Ah',
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: {
            fontFamily: 'Roboto',
            fontSize: 12,
            color: '#ccc'
          },
          min: 0,
          max: maxCapacity * 1.05,
          axisLine: { lineStyle: { color: '#444' } },
          axisLabel: {
            fontFamily: 'Roboto',
            fontSize: 11,
            color: '#999',
            formatter: (value) => value.toFixed(1)
          },
          splitLine: {
            lineStyle: { color: 'rgba(255,255,255,0.05)' }
          }
        },
        yAxis: {
          type: 'value',
          name: yAxisLabel,
          nameLocation: 'middle',
          nameGap: 45,
          nameTextStyle: {
            fontFamily: 'Roboto',
            fontSize: 12,
            color: '#ccc',
            rich: {
              sub: {
                fontSize: 9,
                verticalAlign: 'bottom',
                padding: [3, 0, 0, 0]
              }
            }
          },
          min: showTemperature 
            ? (DCC_Ivar_T_amb[0] ? DCC_Ivar_T_amb[0] - 5 : 20) 
            : ((cellData?.electrical?.V_min ?? 2.5) - 0.1),
          max: showTemperature 
            ? (maxValue !== -Infinity ? maxValue + 5 : 40) 
            : ((cellData?.electrical?.V_max ?? 4.2) + 0.1),
          axisLine: { lineStyle: { color: '#444' } },
          axisLabel: {
            fontFamily: 'Roboto',
            fontSize: 11,
            color: '#999',
            showMaxLabel: false
          },
          splitLine: {
            lineStyle: { color: 'rgba(255,255,255,0.05)' }
          }
        },
        series: seriesData
      }

      chartInstance.current.setOption(option)

      // Synchronize hover across all registered charts
      chartInstance.current.on('mousemove', (params) => {
        if (params.componentType === 'series') {
          Object.keys(chartRegistry).forEach(key => {
            if (key !== plotId && chartRegistry[key]) {
              chartRegistry[key].dispatchAction({
                type: 'showTip',
                seriesIndex: params.seriesIndex,
                dataIndex: params.dataIndex
              })
            }
          })
        }
      })

      chartInstance.current.on('mouseout', () => {
        Object.keys(chartRegistry).forEach(key => {
          if (key !== plotId && chartRegistry[key]) {
            chartRegistry[key].dispatchAction({
              type: 'hideTip'
            })
          }
        })
      })
    }

    // Cleanup on unmount
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose()
        delete chartRegistry[plotId]
        chartInstance.current = null
      }
    }
  }, [cellData, plotId, showLegend, showTemperature])

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current) chartInstance.current.resize()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <div>
      {cellData?.electrical?.DCC_Ivar_t?.length > 0 ? (
        <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: '600px' }}></div>
      ) : (
        <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
          No discharge data available
        </div>
      )}
    </div>
  )
}
